import { h, JSX } from 'preact'
import { StateUpdater, useRef, useState } from 'preact/hooks'

import { CurrencyValueForm } from 'models/apps'

import { validateCurrencyAmountOnInput, formatCurrencyAmount } from 'apps-utils/validate-currency-input'
import { CUURENCY_AMOUNT_INPUT_MAX_INTEGER_LENGTH } from 'apps-calculators/exchange-calculator/config'
import { debounce } from 'utils/functions'

interface CalculatorCurrencyInputProps {
  value: CurrencyValueForm
  setValue: StateUpdater<CurrencyValueForm>
  isBelowMinimalAmount?: boolean
  debounceTime: number
  id?: string
}

export const CalculatorCurrencyInput = ({
  value,
  setValue,
  isBelowMinimalAmount,
  debounceTime,
  id,
}: CalculatorCurrencyInputProps) => {
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const [localValue, setLocalValue] = useState<string>('')
  const savedValueRef = useRef<CurrencyValueForm | null>(null)
  const debouncedSetValue = useRef(debounce((val: CurrencyValueForm) => setValue(val), debounceTime)).current

  const handleAmountOnFocus = (): void => {
    savedValueRef.current = value
    setLocalValue('')
    setIsFocused(true)
  }

  const handleAmountOnInput = (event: JSX.TargetedEvent<HTMLInputElement, Event>): void => {
    const inputValue = event.currentTarget.value
    const validatedValue = validateCurrencyAmountOnInput(inputValue, CUURENCY_AMOUNT_INPUT_MAX_INTEGER_LENGTH)
    setLocalValue(validatedValue.currentValueFormatted ?? '')
    debouncedSetValue(validatedValue)
  }

  const handleAmountOnBlur = (event: JSX.TargetedEvent<HTMLInputElement, Event>): void => {
    setIsFocused(false)
    const inputValue = event.currentTarget.value
    if (!inputValue && savedValueRef.current) {
      setValue(savedValueRef.current)
    } else {
      const formattedValue = formatCurrencyAmount(inputValue)
      setValue(formattedValue)
    }
    savedValueRef.current = null
  }

  return (
    <div className="preact-currency-input">
      <input
        id={id ? id : 'exchange-calculator__input'}
        className={`preact-currency-input__field ${isBelowMinimalAmount ? 'is-error' : ''}`}
        type="tel"
        inputMode="decimal"
        placeholder=""
        value={isFocused ? localValue : value.currentValueFormatted}
        onFocus={handleAmountOnFocus}
        onInput={handleAmountOnInput}
        onBlur={handleAmountOnBlur}
        maxLength={13}
        data-calculator-value
      />
    </div>
  )
}
